const ALLOWED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/tiff',
  'image/heic',
  'image/heif',
  'image/x-canon-cr2',
  'image/x-nikon-nef',
] as const;

/** Accepted MIME types as a plain Set for fast lookup. */
const ALLOWED_SET = new Set<string>(ALLOWED_TYPES);

/** Map common raw/heic extensions to their MIME types for fallback detection. */
const EXTENSION_MAP: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  heic: 'image/heic',
  heif: 'image/heif',
  cr2: 'image/x-canon-cr2',
  nef: 'image/x-nikon-nef',
};

const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200 MB

/**
 * Resolve a file's effective MIME type. Browsers may return an empty string
 * for RAW / HEIC files, so we fall back to extension-based detection.
 */
function resolveType(file: File): string {
  if (file.type && file.type !== 'application/octet-stream') {
    return file.type;
  }
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return EXTENSION_MAP[ext] ?? '';
}

export interface FileValidationResult {
  valid: boolean;
  error?: string;
}

export function validateFile(file: File): FileValidationResult {
  const mime = resolveType(file);

  if (!ALLOWED_SET.has(mime)) {
    return {
      valid: false,
      error: `Unsupported file type "${mime || 'unknown'}". Allowed: JPEG, PNG, TIFF, HEIC, CR2, NEF.`,
    };
  }

  if (file.size > MAX_FILE_SIZE) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return {
      valid: false,
      error: `File is too large (${sizeMB} MB). Maximum allowed size is 200 MB.`,
    };
  }

  if (file.size === 0) {
    return { valid: false, error: 'File is empty (0 bytes).' };
  }

  return { valid: true };
}

export interface BatchValidationResult {
  valid: File[];
  invalid: { file: File; error: string }[];
}

export function validateFiles(files: File[]): BatchValidationResult {
  const valid: File[] = [];
  const invalid: { file: File; error: string }[] = [];

  for (const file of files) {
    const result = validateFile(file);
    if (result.valid) {
      valid.push(file);
    } else {
      invalid.push({ file, error: result.error! });
    }
  }

  return { valid, invalid };
}

export { ALLOWED_TYPES, MAX_FILE_SIZE };
